import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useShallow } from "zustand/react/shallow";
import { TRUCK_RACE, type TruckFastestLap, type TruckLapBoards, type TrucksState, type TruckRacerView } from "@townsy/contracts";
import { TRUCK_CHECKPOINTS, truckEntry } from "@townsy/world";
import { useTownSession } from "../../app/TownSessionContext";
import { useTownStore } from "../../state/store";
import type { VenueHudProps } from "../types";
import { localTruck } from "./localTruck";
import { TRUCK_PAINTS } from "./palette";
import { finishMemory, lapLabel, myRacer, ordinal, raceBanner, type FinishView, type RaceBanner } from "./raceHud";
import { asLapBoards, asTrucksState, formatLapTime } from "./state";
import "./trucks.css";

export interface TrucksHudViewProps {
  state: TrucksState | null;
  localActorId: string | null;
  /** The truck the visitor drives (ActorState.vehicleId), or null on foot. */
  drivingId: string | null;
  /** The truck at whose driver's door the visitor stands, or null. */
  candidateId: string | null;
  pending: "enter" | "exit" | null;
  connected: boolean;
  /** Server clock now (ms), for lap and race timers. */
  serverNow: number;
  /** The driven truck's speed (m/s, predicted) when driving. */
  speed: number | null;
  isTouch: boolean;
  /** Latest notice for this visitor (a refusal or a lap / race message), shown for a few seconds. */
  notice: { message: string; tone: "warning" | "info" } | null;
  /** The fastest laps boards (this visitor's `venue.private`), or null before they arrive. */
  boards: TruckLapBoards | null;
  /** The finish screen to show (finishMemory), or null. */
  finish: FinishView | null;
  onGetIn(): void;
  onGetOut(): void;
  /** "Start a race", "Join the race" and "Race again" (one request: the server opens or joins and puts you on the grid). */
  onStartRace(): void;
  /** "Free drive": close the finish screen. */
  onDismissFinish(): void;
}

/** Checkpoints between the start / finish line and itself (the line is checkpoint 0). */
const CHECKPOINTS = TRUCK_CHECKPOINTS.length - 1;

function TruckChip({ id }: { id: string }) {
  const e = truckEntry(id);
  if (!e) return null;
  const paint = TRUCK_PAINTS[e.paint % TRUCK_PAINTS.length]!;
  return (
    <span className="truck-chip" style={{ background: paint.body, color: paint.text }} aria-label={`Truck ${e.number}`}>
      {e.number}
    </span>
  );
}

export function AiBadge() {
  return (
    <span className="trucks-ai" title="AI racer: a computer-driven truck, not a real visitor" aria-label="AI racer">
      AI
    </span>
  );
}

function RaceLine({ r, mine, laps }: { r: TruckRacerView; mine: boolean; laps: number }) {
  return (
    <li className={mine ? "mine" : undefined}>
      <span className="pos">{r.position}.</span>
      <TruckChip id={r.truckId} />
      <span className="who">
        {mine ? "You" : r.label}
        {r.ai ? <AiBadge /> : null}
      </span>
      <span className="time">{r.dnf ? "DNF" : r.finishMs !== null ? formatLapTime(r.finishMs) : lapLabel(r, laps)}</span>
    </li>
  );
}

function BoardList({ title, lines, localActorId, testId }: { title: string; lines: TruckFastestLap[]; localActorId: string | null; testId: string }) {
  return (
    <div className="trucks-board-col" data-testid={testId}>
      <strong>{title}</strong>
      {lines.length === 0 ? (
        <span className="muted">No laps yet.</span>
      ) : (
        <ol className="trucks-board">
          {lines.map((l, i) => (
            <li key={l.actorId} className={l.actorId === localActorId ? "mine" : undefined}>
              <span className="pos">{i + 1}.</span>
              <span className="who">
                {l.label}
                {l.actorId === localActorId ? " (you)" : ""}
              </span>
              <span className="time">{formatLapTime(l.lapMs)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * The fastest laps boards (ADR-0032), kept in the database: today's (UTC) and all time, ten lines each, one per
 * driver, people only. Folded to the two leaders so the panel stays short; open it for both lists.
 */
export function LapBoardsView({ boards, localActorId }: { boards: TruckLapBoards; localActorId: string | null }) {
  const lead = (lines: TruckFastestLap[]) => (lines[0] ? `${lines[0].label} ${formatLapTime(lines[0].lapMs)}` : "–");
  return (
    <details className="trucks-boards small" data-testid="trucks-boards">
      <summary>
        Fastest laps · today: <strong>{lead(boards.today)}</strong> · all time: <strong>{lead(boards.allTime)}</strong>
      </summary>
      <div className="trucks-boards-cols">
        <BoardList title="Fastest laps today (UTC)" lines={boards.today} localActorId={localActorId} testId="trucks-board-today" />
        <BoardList title="All-time fastest laps" lines={boards.allTime} localActorId={localActorId} testId="trucks-board-alltime" />
      </div>
      <p className="muted trucks-boards-note">People only: AI racers are not on the boards. Nothing is won.</p>
    </details>
  );
}

/** Checkpoints passed this lap as dots (not "n/4", which read like a lap count: ADR-0032). */
function CheckpointDots({ next }: { next: number }) {
  const passed = next === 0 ? CHECKPOINTS : Math.max(0, next - 1);
  return (
    <span className="trucks-cps" role="img" aria-label={`Checkpoints passed this lap: ${passed} of ${CHECKPOINTS}`} title="Checkpoints passed this lap">
      {Array.from({ length: CHECKPOINTS }, (_, i) => (
        <span key={i} className={i < passed ? "on" : undefined} />
      ))}
    </span>
  );
}

/** Start lights for the countdown overlay: two red at 3, four at 2, all five at 1, all green at GO. */
function Lights({ count }: { count: number | "go" }) {
  const lit = count === "go" ? 5 : count >= 3 ? 2 : count === 2 ? 4 : 5;
  return (
    <span className="trucks-lights" aria-hidden="true">
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={count === "go" ? "green" : i < lit ? "red" : undefined} />
      ))}
    </span>
  );
}

/** The big race line at the top of the screen, and the 3-2-1-GO in the middle. Decorative copies of the panel's text. */
export function RaceBannerView({ banner }: { banner: RaceBanner }) {
  switch (banner.kind) {
    case "none":
      return null;
    case "lobby":
      return (
        <div className="trucks-banner" data-testid="trucks-banner" data-kind="lobby">
          <strong>Race starting in {banner.seconds}…</strong>
          <span>{banner.onGrid ? "You're on the grid." : "Join: drive onto the grid, or press Join the race."}</span>
        </div>
      );
    case "countdown":
      return (
        <div className="trucks-countdown-big" data-testid="trucks-countdown" role="status" aria-live="assertive" aria-label={`Race starts in ${banner.count}`}>
          <Lights count={banner.count} />
          <span className="n">{banner.count}</span>
        </div>
      );
    case "go":
      return (
        <div className="trucks-countdown-big go" data-testid="trucks-countdown" role="status" aria-live="assertive">
          <Lights count="go" />
          <span className="n">GO!</span>
        </div>
      );
    case "racing":
      return (
        <div className="trucks-banner racing" data-testid="trucks-banner" data-kind="racing">
          <span className="place">
            <strong>{ordinal(banner.position)}</strong> of {banner.of}
          </span>
          <span className="lap">
            Lap <strong>{banner.lap}</strong> of {banner.laps}
          </span>
          <span className="times">
            {banner.lapMs !== null ? (
              <>
                Lap time <strong>{formatLapTime(banner.lapMs)}</strong>
              </>
            ) : (
              <>
                Race <strong>{formatLapTime(banner.raceMs)}</strong>
              </>
            )}
            {" · "}Best {formatLapTime(banner.bestLapMs)}
          </span>
        </div>
      );
    case "watching":
      return (
        <div className="trucks-banner" data-testid="trucks-banner" data-kind="watching">
          <span>
            Race on: {banner.racers} trucks{banner.leader ? ` · ${banner.leader} leads` : ""}
          </span>
        </div>
      );
  }
}

/** The finish screen: your place, time and best lap, the full results with AI racers marked, then race again or drive. */
export function FinishCard(p: { finish: FinishView; canRaceAgain: boolean; raceAgainWhy: string; onRaceAgain(): void; onFreeDrive(): void }) {
  const f = p.finish;
  return (
    <section className="trucks-finish" role="dialog" aria-modal="false" aria-labelledby="trucks-finish-title" data-testid="trucks-finish" data-place={f.dnf ? "dnf" : f.place}>
      <p className="kicker">{f.final ? "Race results" : "You're home: the others are still racing"}</p>
      <h2 id="trucks-finish-title" className={f.place === 1 && !f.dnf ? "win" : undefined}>
        {f.headline}
      </h2>
      <p className="stats">
        {f.finishMs !== null ? (
          <span>
            Time <strong>{formatLapTime(f.finishMs)}</strong>
          </span>
        ) : null}
        <span>
          Best lap <strong>{formatLapTime(f.bestLapMs)}</strong>
        </span>
      </p>
      <table className="trucks-results" data-testid="trucks-finish-results">
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Driver</th>
            <th scope="col">Time</th>
            <th scope="col">Best lap</th>
          </tr>
        </thead>
        <tbody>
          {f.rows.map((r) => (
            <tr key={`${r.position}-${r.truckId}`} className={r.mine ? "mine" : undefined}>
              <td>{r.position}</td>
              <td>
                <TruckChip id={r.truckId} /> {r.mine ? "You" : r.label}
                {r.ai ? <AiBadge /> : null}
              </td>
              <td>{r.time}</td>
              <td>{r.best}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="small muted">Just for fun: nothing is won. A driver not home {TRUCK_RACE.finishWindowMs / 1000} s after the winner is DNF.</p>
      <div className="trucks-row trucks-finish-actions">
        <button type="button" className="btn btn-primary" disabled={!p.canRaceAgain} title={p.canRaceAgain ? "Back onto the starting grid for the next race" : p.raceAgainWhy} onClick={p.onRaceAgain} data-testid="trucks-race-again">
          Race again
        </button>
        <button type="button" className="btn" onClick={p.onFreeDrive} data-testid="trucks-free-drive">
          Free drive
        </button>
        {!p.canRaceAgain ? <span className="small muted">{p.raceAgainWhy}</span> : null}
      </div>
    </section>
  );
}

/** Why "Race again" cannot be pressed right now ("" when it can). */
function raceAgainBlocked(p: TrucksHudViewProps): string {
  if (!p.connected) return "Reconnecting…";
  if (!p.drivingId) return "Get into a truck to race again.";
  const phase = p.state?.race.phase ?? "idle";
  if (phase === "racing" || phase === "countdown") return `Wait for the others: the race ends at most ${TRUCK_RACE.finishWindowMs / 1000} s after the winner.`;
  return "";
}

/**
 * Full-screen overlays live on the page, not inside the venue panel (a scrolling panel left a ghost of itself behind a
 * fixed child). Without a DOM (the HUD tests render to a string) they render in place.
 */
function OnPage({ children }: { children: ReactNode }) {
  if (typeof document === "undefined") return <>{children}</>;
  return createPortal(children, document.body);
}

/** The race overlays (top banner, 3-2-1-GO, finish screen): shown with the panel open or collapsed. */
export function RaceOverlays(p: TrucksHudViewProps) {
  const banner = raceBanner(p.state, p.localActorId, p.serverNow);
  const why = raceAgainBlocked(p);
  return (
    <OnPage>
      <RaceBannerView banner={banner} />
      {p.finish ? (
        <FinishCard
          finish={p.finish}
          canRaceAgain={why === ""}
          raceAgainWhy={why}
          onRaceAgain={() => {
            p.onDismissFinish();
            p.onStartRace();
          }}
          onFreeDrive={p.onDismissFinish}
        />
      ) : null}
    </OnPage>
  );
}

/**
 * Monster Truck Arena overlay (ADR-0016, ADR-0032): get in / out, speed, lap timing, races against people and AI
 * racers, and the fastest laps boards (today and all time, kept in the database). A pure view of the server's state
 * (the race, places, laps and boards are decided on the server); the buttons only send requests.
 */
export function TrucksHudView(p: TrucksHudViewProps) {
  const s = p.state;
  const me = s?.drivers.find((d) => d.actorId === p.localActorId) ?? null;
  const race = s?.race ?? null;
  const remaining = (at: number | null | undefined) => (at ? Math.max(0, Math.ceil((at - p.serverNow) / 1000)) : 0);
  const racer = myRacer(race, p.localActorId);
  const driving = p.drivingId !== null;
  const laps = race?.laps ?? TRUCK_RACE.laps;
  const field = race?.field ?? TRUCK_RACE.field;
  const aiLine = field > 1 ? `${laps} laps against up to ${field - 1} AI trucks; other drivers can join.` : `${laps} laps. Others join on the starting grid.`;
  const inRace = racer !== null && (race?.phase === "countdown" || race?.phase === "racing");
  // Racing (the banner carries lap, place and times) or home with the finish screen up: a shorter panel.
  const compact = (inRace && racer !== null && racer.finishMs === null && !racer.dnf) || p.finish !== null;

  let raceBlock: React.ReactNode = null;
  if (!race || race.phase === "idle") {
    raceBlock = (
      <div className="trucks-row">
        <button
          type="button"
          className="btn btn-sm"
          disabled={!driving || !p.connected}
          onClick={p.onStartRace}
          title={driving ? "Opens a 10 second lobby and puts you on the starting grid" : "Get into a truck to start a race"}
          data-testid="trucks-start-race"
        >
          Start a race
        </button>
        <span className="small muted">{driving ? aiLine : "Get into a truck to start a race."}</span>
      </div>
    );
  } else if (race.phase === "lobby") {
    const people = race.racers.length;
    raceBlock = (
      <div className="trucks-race lobby" role="status" data-testid="trucks-race" data-phase="lobby">
        <strong>Race starting in {remaining(race.endsAt)}…</strong>
        <span className="small">
          {racer ? "You're on the grid. " : "Join: drive onto the starting grid behind the start line, or press Join the race. "}
          {people === 0 ? "Nobody on the grid yet." : `${people} ${people === 1 ? "driver" : "drivers"} on the grid.`}
          {field > people ? " AI trucks fill the empty places." : ""}
        </span>
        {!racer ? (
          <div className="trucks-row">
            <button type="button" className="btn btn-sm btn-primary" disabled={!driving || !p.connected} onClick={p.onStartRace} title={driving ? "Puts your truck on a free place on the grid" : "Get into a truck to race"} data-testid="trucks-join-race">
              Join the race
            </button>
          </div>
        ) : null}
      </div>
    );
  } else if (race.phase === "countdown") {
    const n = Math.max(1, remaining(race.endsAt));
    raceBlock = (
      <div className="trucks-race countdown" role="status" data-testid="trucks-race" data-phase="countdown">
        <span className="trucks-count" aria-label={`Race starts in ${n}`}>
          {n}
        </span>
        <span className="small">{racer ? "Hold on: your truck is on the grid." : `${race.racers.length} trucks on the grid.`}</span>
      </div>
    );
  } else if (race.phase === "racing") {
    raceBlock = (
      <div className="trucks-race racing" role="status" data-testid="trucks-race" data-phase="racing">
        {racer ? (
          <strong>
            {racer.finishMs !== null
              ? `Finished ${ordinal(racer.position)} in ${formatLapTime(racer.finishMs)}`
              : racer.dnf
                ? "Out of the race"
                : `${lapLabel(racer, laps)} · ${ordinal(racer.position)} of ${race.racers.length}`}
          </strong>
        ) : (
          <strong>Race on: {race.racers.length} trucks</strong>
        )}
        {p.finish ? (
          <span className="small">The others are still racing: the board is on screen.</span>
        ) : (
          <ol className="trucks-board">
            {race.racers.map((r) => (
              <RaceLine key={r.actorId} r={r} mine={r.actorId === p.localActorId} laps={laps} />
            ))}
          </ol>
        )}
      </div>
    );
  } else if (p.finish) {
    // The finish screen shows the results: the panel only says how long they last.
    raceBlock = (
      <div className="trucks-race results" role="status" data-testid="trucks-race" data-phase="results">
        <strong>Race over</strong>
        <span className="small muted">Free driving again in {remaining(race.endsAt)} s. Just for fun: nothing is won.</span>
      </div>
    );
  } else {
    raceBlock = (
      <div className="trucks-race results" role="status" data-testid="trucks-race" data-phase="results">
        <strong>Race results</strong>
        <ol className="trucks-board" data-testid="trucks-results">
          {race.racers.map((r) => (
            <RaceLine key={r.actorId} r={r} mine={r.actorId === p.localActorId} laps={laps} />
          ))}
        </ol>
        <div className="trucks-row">
          <button type="button" className="btn btn-sm" disabled={!driving || !p.connected} onClick={p.onStartRace} data-testid="trucks-start-race">
            Race again
          </button>
          <span className="small muted">Free driving again in {remaining(race.endsAt)} s. Just for fun: nothing is won.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="venue-hud-body trucks-hud" data-testid="trucks-hud">
      {driving ? (
        <div className="trucks-row trucks-drive" data-testid="trucks-driving">
          <TruckChip id={p.drivingId!} />
          <span className="trucks-speed" aria-label="Speed">
            {Math.round(Math.abs(p.speed ?? 0) * 3.6)} <small>km/h</small>
          </span>
          <span className="trucks-lap" data-testid="trucks-lap">
            {inRace && racer && !racer.dnf && racer.finishMs === null ? <strong>{lapLabel(racer, laps)}</strong> : null}
            {inRace && racer && !racer.dnf && racer.finishMs === null ? " · " : null}
            {me?.lapStartedAt ? (
              <>
                Lap time <strong>{formatLapTime(p.serverNow - me.lapStartedAt)}</strong> <CheckpointDots next={me.nextCheckpoint} />
              </>
            ) : inRace ? (
              "Cross the start line: lap 1 starts there."
            ) : (
              "Free drive: cross the start line to time a lap."
            )}
          </span>
          <button type="button" className="btn btn-sm btn-primary" disabled={!p.connected || p.pending === "exit"} onClick={p.onGetOut} data-testid="trucks-get-out">
            {p.pending === "exit" ? "Getting out…" : "Get out (E)"}
          </button>
        </div>
      ) : (
        <div className="trucks-row" data-testid="trucks-walking">
          {p.candidateId ? (
            <button type="button" className="btn btn-primary" disabled={!p.connected || p.pending !== null} onClick={p.onGetIn} data-testid="trucks-get-in">
              {p.pending === "enter" ? "Getting in…" : `Get in truck ${truckEntry(p.candidateId)?.number ?? ""} (E)`}
            </button>
          ) : null}
          <span className="small">
            {p.candidateId ? "Free to drive. Your best laps go on the Fastest laps boards." : "Walk onto a boarding pier beside a truck's driver door (its left side) to drive it. Free, no chips."}
          </span>
        </div>
      )}
      {!compact && me && (me.lastLapMs !== null || me.bestLapMs !== null) ? (
        <div className="trucks-row small" data-testid="trucks-times">
          <span>
            Last lap <strong>{formatLapTime(me.lastLapMs)}</strong>
          </span>
          <span>
            Best lap <strong>{formatLapTime(me.bestLapMs)}</strong>
          </span>
          <span className="muted">
            {me.laps} timed {me.laps === 1 ? "lap" : "laps"} this visit
          </span>
        </div>
      ) : null}
      {raceBlock}
      {p.boards && race?.phase !== "racing" && race?.phase !== "results" ? <LapBoardsView boards={p.boards} localActorId={p.localActorId} /> : null}
      {p.notice ? (
        <p className={`trucks-notice ${p.notice.tone}`} role="status" data-testid="trucks-notice">
          {p.notice.message}
        </p>
      ) : null}
      {!compact ? (
        <p className="small muted trucks-keys">
          {p.isTouch
            ? "Joystick up: gas · down: brake / reverse · sideways: steer. Jumps launch you higher the faster you hit them."
            : "W/↑ gas · S/↓ brake, then reverse · A/D or ←/→ steer · E get in / out. Hit the ramps fast to fly; mud slows you down."}
        </p>
      ) : null}
      <RaceOverlays {...p} />
    </div>
  );
}

const NOTICE_MS = 6000;

/** Server clock from the shared state's `now`: the least-delayed sample gives the offset to the local clock. */
function useServerClock(state: TrucksState | null): number {
  const ref = useRef<{ offset: number | null; seen: number }>({ offset: null, seen: -1 });
  if (state && state.now !== ref.current.seen) {
    ref.current.seen = state.now;
    const o = state.now - Date.now();
    ref.current.offset = ref.current.offset === null ? o : Math.max(ref.current.offset, o);
  }
  return Date.now() + (ref.current.offset ?? 0);
}

/** Everything the arena's overlay needs from the session and the stores (shared by the panel and its collapsed strip). */
function useTrucksHudProps(props: VenueHudProps): TrucksHudViewProps {
  const session = useTownSession();
  const ui = useTownStore(
    useShallow((s) => ({
      drivingId: s.ui.drivingVehicleId,
      candidateId: s.ui.vehicleCandidateId,
      pending: s.ui.vehiclePending,
      isTouch: s.ui.isTouch,
      joined: s.room.status === "joined",
    })),
  );
  const state = asTrucksState(props.shared);
  const serverNow = useServerClock(state);
  const [, setTick] = useState(0);
  const me = state?.drivers.find((d) => d.actorId === props.localActorId);
  const phase = state?.race.phase ?? "idle";
  const finish = finishMemory.current(state?.race, props.localActorId);
  const ticking = ui.drivingId !== null || Boolean(me?.lapStartedAt) || phase !== "idle";
  useEffect(() => {
    if (!ticking) return;
    const id = window.setInterval(() => setTick((n) => (n + 1) % 1_000_000), 100);
    return () => window.clearInterval(id);
  }, [ticking]);
  const latest = props.events[props.events.length - 1];
  const notice = latest && Date.now() - latest.at < NOTICE_MS ? { message: latest.message, tone: latest.type === "rejected" ? ("warning" as const) : ("info" as const) } : null;
  const local = localTruck.get();
  const speed = ui.drivingId ? (local && local.vehicleId === ui.drivingId ? local.current.speed : (state?.trucks.find((t) => t.id === ui.drivingId)?.speed ?? 0)) : null;
  return {
    state,
    localActorId: props.localActorId,
    drivingId: ui.drivingId,
    candidateId: ui.candidateId,
    pending: ui.pending,
    connected: ui.joined && session !== null,
    serverNow,
    speed,
    isTouch: ui.isTouch,
    notice,
    boards: asLapBoards(props.mine),
    finish,
    onGetIn: () => session?.interactWithVehicle(),
    onGetOut: () => session?.exitVehicle(),
    onStartRace: () => props.send({ type: "race.start" }),
    onDismissFinish: () => {
      finishMemory.dismiss();
      setTick((n) => (n + 1) % 1_000_000);
    },
  };
}

export function TrucksHud(props: VenueHudProps) {
  return <TrucksHudView {...useTrucksHudProps(props)} />;
}

/** The collapsed panel ("Hide"): the race overlays stay on screen, with a one-line race status. */
export function TrucksCompact(props: VenueHudProps) {
  const p = useTrucksHudProps(props);
  const race = p.state?.race ?? null;
  const racer = myRacer(race, p.localActorId);
  const line =
    race?.phase === "racing" && racer && racer.finishMs === null && !racer.dnf
      ? `${ordinal(racer.position)} of ${race.racers.length} · ${lapLabel(racer, race.laps)}`
      : race?.phase === "lobby"
        ? `Race starting in ${Math.max(0, Math.ceil(((race.endsAt ?? p.serverNow) - p.serverNow) / 1000))}…`
        : null;
  return (
    <div className="trucks-compact" data-testid="trucks-compact">
      {line ? <span className="small">{line}</span> : null}
      <RaceOverlays {...p} />
    </div>
  );
}
